import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Sparkles, Target, Plus, Search, TrendingUp, BarChart3, GripVertical, ListTodo, Activity, Shield, MessageSquare, Clock, Zap, MousePointer } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LEADS_STEPS = [
  {
    id: 'leads-welcome',
    title: 'Welcome to Lead Pipeline',
    description: 'This is your CRM command centre. Track prospects from first inquiry through to conversion. Let\'s walk through the key features.',
    icon: Target,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-tab',
    title: 'Leads Tab',
    description: 'The Leads tab is where all your prospect management lives. Switch between Leads, Clients, and Analytics tabs from the top navigation.',
    icon: Target,
    target: '[data-testid="tab-leads"]',
    position: 'bottom'
  },
  {
    id: 'leads-stats',
    title: 'Quick Stats Overview',
    description: 'At a glance, see your Active Leads count, total Pipeline Value, Won This Month, and Conversion Rate. These update in real-time as you move leads through stages.',
    icon: BarChart3,
    target: '[data-testid="leads-quick-stats"]',
    position: 'bottom'
  },
  {
    id: 'leads-add',
    title: 'Adding New Leads',
    description: 'Click "Add Lead" to create a new prospect. Enter their name, company, contact details, estimated value, and source. Leads start in the "New Inquiry" stage.',
    icon: Plus,
    target: '[data-testid="add-lead-btn"]',
    position: 'bottom'
  },
  {
    id: 'leads-search',
    title: 'Search & Filter',
    description: 'Quickly find any lead by name, company, or contact details. The search filters across all pipeline stages instantly.',
    icon: Search,
    target: '[data-testid="leads-search"]',
    position: 'bottom'
  },
  {
    id: 'leads-funnel',
    title: 'Pipeline Funnel',
    description: 'Toggle the funnel view to see a visual breakdown of how leads flow through each stage. Great for spotting bottlenecks in your conversion process.',
    icon: TrendingUp,
    target: '[data-testid="funnel-toggle-btn"]',
    position: 'bottom'
  },
  {
    id: 'leads-kanban',
    title: 'Kanban Board',
    description: 'Your pipeline is displayed as a Kanban board with six stages: New Inquiry, Qualified, Proposal Sent, Negotiating, Won, and Lost. Drag and drop leads between stages to update their progress.',
    icon: GripVertical,
    target: '[data-testid="kanban-board"]',
    position: 'top',
    fallbackTarget: '[data-testid="stage-column-new"]'
  },
  {
    id: 'leads-stages',
    title: 'Stage Columns',
    description: 'Each column shows the number of leads, total value, and average time in stage. Colour-coded aging indicators on lead cards highlight those needing attention — green is fresh, red means stale.',
    icon: Clock,
    target: '[data-testid="stage-column-new"]',
    position: 'right'
  },
  {
    id: 'leads-card',
    title: 'Lead Cards',
    description: 'Click any lead card to open its detail view. Each card shows the contact name, company, estimated value, source, and an AI qualification score badge. Aging indicators show how long the lead has been in its current stage.',
    icon: MousePointer,
    target: '[data-testid^="lead-card-"]',
    position: 'right',
    fallbackTarget: '[data-testid="stage-column-new"]'
  },
  {
    id: 'leads-detail-tabs',
    title: 'Lead Detail Tabs',
    description: 'Inside a lead, you\'ll find six powerful tabs:\n\n• Details — Contact info, value, stage\n• Tasks — Track follow-ups and to-dos\n• Activity — Communication timeline\n• Qualification — BANT scoring & compliance\n• AI Score — AI-powered lead qualification\n• Notes — Add context and observations',
    icon: ListTodo,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-tasks',
    title: 'Task Management',
    description: 'Create tasks linked to leads with due dates, priorities, and categories. Use templates like "Discovery Call Prep" or "Document Collection" for quick task creation. Never miss a follow-up.',
    icon: ListTodo,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-activity',
    title: 'Activity Timeline',
    description: 'Log every interaction — calls, meetings, emails, LinkedIn messages, and more. Record outcomes, duration, and next steps. Build a complete communication history for each lead.',
    icon: Activity,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-qualification',
    title: 'BANT Qualification',
    description: 'Score leads using the BANT framework: Budget (wealth band), Authority (decision maker), Need (service interests), and Timeline. Auto-calculated scores from 0-100 help prioritise your pipeline.',
    icon: Shield,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-ai',
    title: 'AI Lead Scoring',
    description: 'Get AI-powered qualification scores with confidence levels, key factor analysis, and recommended next actions. AI scores appear as badges on lead cards in the Kanban view.',
    icon: Sparkles,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-automation',
    title: 'Tips & Automation',
    description: 'Automation rules run in the background — auto-assigning referrals, flagging stale leads, and alerting on high-value prospects. Configure rules from the lead analytics section.',
    icon: Zap,
    target: null,
    position: 'center'
  },
  {
    id: 'leads-complete',
    title: 'You\'re All Set!',
    description: 'You now know your way around the Lead Pipeline. Start by adding your first lead or exploring the Kanban board. You can restart this tour anytime from the tour button.',
    icon: Sparkles,
    target: null,
    position: 'center'
  }
];

export default function LeadsOnboarding({ isOpen, onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [highlightStyle, setHighlightStyle] = useState({});
  const tooltipRef = useRef(null);
  const handleNextRef = useRef(null);
  const handlePrevRef = useRef(null);

  const step = LEADS_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === LEADS_STEPS.length - 1;
  const Icon = step?.icon || Sparkles;

  useEffect(() => {
    if (!isOpen || !step) return;

    const positionTooltip = () => {
      if (step.position === 'center' || !step.target) {
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        });
        setHighlightStyle({});
        return;
      }

      let targetEl = document.querySelector(step.target);
      if (!targetEl && step.fallbackTarget) {
        targetEl = document.querySelector(step.fallbackTarget);
      }

      if (!targetEl) {
        setTooltipStyle({
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 10001
        });
        setHighlightStyle({});
        return;
      }

      const rect = targetEl.getBoundingClientRect();
      const padding = 8;

      setHighlightStyle({
        position: 'fixed',
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        zIndex: 10000
      });

      const tooltipWidth = 380;
      const tooltipHeight = 240;
      let top, left;

      switch (step.position) {
        case 'right':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + 16;
          break;
        case 'left':
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - 16;
          break;
        case 'bottom':
          top = rect.bottom + 16;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'top':
          top = rect.top - tooltipHeight - 16;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        default:
          top = rect.bottom + 16;
          left = rect.left;
      }

      if (left < 16) left = 16;
      if (left + tooltipWidth > window.innerWidth - 16) left = window.innerWidth - tooltipWidth - 16;
      if (top < 16) top = 16;
      if (top + tooltipHeight > window.innerHeight - 16) top = window.innerHeight - tooltipHeight - 16;

      setTooltipStyle({
        position: 'fixed',
        top,
        left,
        zIndex: 10001
      });
    };

    const timer = setTimeout(positionTooltip, 100);
    window.addEventListener('resize', positionTooltip);
    window.addEventListener('scroll', positionTooltip);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', positionTooltip);
      window.removeEventListener('scroll', positionTooltip);
    };
  }, [currentStep, isOpen, step]);

  // Keyboard accessibility
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          handleNextRef.current?.();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          handlePrevRef.current?.();
          break;
        case 'Escape':
          event.preventDefault();
          onSkip();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onSkip]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  useEffect(() => {
    handleNextRef.current = handleNext;
    handlePrevRef.current = handlePrev;
  });

  if (!isOpen || !step) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity duration-300"
        style={{ zIndex: 9999 }}
        onClick={onSkip}
        data-testid="leads-onboarding-overlay"
      />

      {/* Highlight box */}
      {highlightStyle.width && (
        <div
          className="rounded-lg ring-4 ring-[#4A3B32] ring-opacity-50 pointer-events-none"
          style={{
            ...highlightStyle,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
            background: 'transparent'
          }}
          data-testid="leads-onboarding-highlight"
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="w-[380px] bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in"
        style={tooltipStyle}
        data-testid="leads-onboarding-tooltip"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4A3B32] to-[#5D524D] text-white px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              <span className="text-xs text-white/70">
                Step {currentStep + 1} of {LEADS_STEPS.length} — Lead Pipeline Tour
              </span>
            </div>
            <button
              onClick={onSkip}
              className="text-white/70 hover:text-white transition-colors"
              data-testid="leads-onboarding-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-heading text-lg">{step.title}</h3>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-[#5D524D] text-sm leading-relaxed mb-4 whitespace-pre-line">
            {step.description}
          </p>

          {/* Progress bar */}
          <div className="w-full h-1.5 bg-[#E6E0D9] rounded-full mb-4 overflow-hidden">
            <div
              className="h-full bg-[#4A3B32] rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / LEADS_STEPS.length) * 100}%` }}
            />
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrev}
              disabled={isFirstStep}
              className="text-[#5D524D] hover:text-[#2C2420]"
              data-testid="leads-onboarding-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onSkip}
              className="text-[#8C8078] hover:text-[#5D524D]"
              data-testid="leads-onboarding-skip-btn"
            >
              Skip Tour
            </Button>

            <Button
              size="sm"
              onClick={handleNext}
              className="bg-[#4A3B32] hover:bg-[#3A2E26] text-white"
              data-testid="leads-onboarding-next-btn"
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>

          {/* Keyboard hint */}
          <div className="mt-3 pt-3 border-t border-[#E6E0D9] text-center">
            <p className="text-xs text-[#8C8078]">
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">&larr;</kbd>
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">&rarr;</kbd>
                <span className="mx-1">Navigate</span>
              </span>
              <span className="mx-2">&middot;</span>
              <span className="inline-flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F5F3F0] rounded border border-[#E6E0D9] font-mono">Esc</kbd>
                <span className="ml-1">Skip</span>
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
